import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import IconConfirmEmail from '@components/Images/Rebranding/IconConfirmEmail';
import useCorporateLeadCreate from '@hooks/useCorporateLeadCreate';
import useEventTracking from '@hooks/useEventTracking';
import useServerSideRendering from '@hooks/useServerSideRendering';
import { RAILS_ENV } from '@src/env';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { LeadType } from '@utils/types';

import LeadForm from './LeadForm';

export interface CorporateLeadFormProps {
  className?: string;
  getInTouchEmailAddress: string;
  includeNumberOfUsers?: boolean;
  leadType: LeadType;
}

const CorporateLeadForm = ({
  className,
  getInTouchEmailAddress,
  includeNumberOfUsers,
  leadType,
}: CorporateLeadFormProps): JSX.Element => {
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const [showSuccessMessage, setSuccessMessage] = useState(false);
  const [existingEntries, setExistingEntries] = useState(false);
  const { mutateAsync: createCorporateLead } = useCorporateLeadCreate();
  const hydrated = useServerSideRendering();
  const { trackEvent } = useEventTracking({
    location: TrackingLocation.CorporateLeadForm,
  });

  const handleSubmit = async ({
    companyName,
    email,
    jobTitle,
    name,
    phoneNumber,
    possibleNumberOfUsers,
  }) => {
    // Send an empty string for reCaptcha token in test environment so it doesn't fail
    try {
      const refToken = RAILS_ENV === 'test' ? '' : await reCaptchaRef.current.executeAsync();
      const sanitizedParams = {
        corporate_lead: {
          company_name: companyName,
          email,
          job_title: jobTitle,
          lead_type: leadType,
          name,
          phone_number: phoneNumber,
          possible_number_of_users: possibleNumberOfUsers?.value,
          recaptcha_token: refToken,
        },
      };
      const resp = await createCorporateLead(sanitizedParams);
      if (resp.success) {
        setExistingEntries(resp.existingEntries);
        setSuccessMessage(!showSuccessMessage);
      }
      reCaptchaRef.current.reset();
      trackEvent(click({ clickTarget: ClickTarget.SubmitButton }));
    } catch (e) {
      console.error('catching error: ', e);
      reCaptchaRef.current.reset();
    }
  };

  return (
    <div
      className={`pb-18 relative mx-auto w-full max-w-[330px] bg-white dark:bg-[var(--background-white)] ${className}`}
      data-testid="corporate-lead-form"
    >
      {!showSuccessMessage ? (
        <div>
          <h3 className="my-0 text-left font-suisse-neue text-subheadline-md text-primary-black">
            Connect with our team
          </h3>
          {hydrated && (
            <LeadForm
              includeNumberOfUsers={includeNumberOfUsers}
              leadType={leadType}
              onSubmit={handleSubmit}
              reCaptchaRef={reCaptchaRef}
            />
          )}
        </div>
      ) : (
        <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
          <h3 className="font-suisse-neue text-subheadline-md text-primary-black">
            Thank you for reaching out!
          </h3>
          <IconConfirmEmail className="mx-auto my-24 w-56" />
          <p className="w-full break-words px-8 font-suisse-intl text-subheadline-sm text-primary-black">
            {existingEntries
              ? 'Thank you for filling out our form. We already have your details and will be reaching out shortly. If you would like to send us any further information, you can reach out to us via email at'
              : 'Someone from our team will be in touch with you shortly. If you have any immediate questions, please reach out to'}{' '}
            <a className="font-bold text-primary-magenta" href={`mailto:${getInTouchEmailAddress}`}>
              {getInTouchEmailAddress}
            </a>
            .
          </p>
        </div>
      )}
    </div>
  );
};

export default CorporateLeadForm;
